import { Info as InfoIcon, Sync as SyncIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import MarketGrowthDataTable, {
  MarketGrowthServerPagination,
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import CopyableValue from "../components/CopyableValue";
import AttributionDetailDialog from "../components/AttributionDetailDialog";
import MarketGrowthExportButton from "../components/MarketGrowthExportButton";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import { toSelectOptions } from "../constants/commissionEnums";
import {
  getAttributionStatusLabel,
  REFERRAL_ATTRIBUTION_STATUS_LABELS,
} from "../constants/reportEnums";
import { formatApiDate } from "../utils/commissionValidation";
import { openMarketGrowthExportLink } from "../utils/exportHelpers";

const PAGE_SIZE = 20;

const formatPersonCell = (name, mobile) => {
  if (!name && !mobile) return "—";
  return (
    <>
      <Typography variant="body2">{name || "—"}</Typography>
      {mobile ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {mobile}
        </Typography>
      ) : null}
    </>
  );
};

const ReferralAttributionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({
    referralCode: "",
    partnerId: "",
    registeredUserId: "",
    relatedProjectId: "",
    status: "",
    fromDate: null,
    toDate: null,
  });
  const [detailRow, setDetailRow] = useState(null);

  const loadPartners = useCallback(async () => {
    const res = await MarketGrowthAdminApi.getPartners();
    const normalized = MarketGrowthAdminApi.normalizeList(
      res,
      "بارگذاری نمایندگان ناموفق بود."
    );
    if (!normalized.error) {
      setPartners(Array.isArray(normalized.data) ? normalized.data : []);
    }
  }, []);

  const buildParams = useCallback(
    () => {
      const params = { page, pageSize: PAGE_SIZE };
      if (filters.referralCode.trim()) params.referralCode = filters.referralCode.trim();
      if (filters.partnerId) params.partnerId = Number(filters.partnerId);
      if (filters.registeredUserId) params.registeredUserId = Number(filters.registeredUserId);
      if (filters.relatedProjectId) params.relatedProjectId = Number(filters.relatedProjectId);
      if (filters.status) params.status = filters.status;
      if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
      if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
      return params;
    },
    [filters, page]
  );

  const buildExportParams = useCallback(() => {
    const params = {};
    if (filters.referralCode.trim()) params.referralCode = filters.referralCode.trim();
    if (filters.partnerId) params.partnerId = Number(filters.partnerId);
    if (filters.status) params.status = filters.status;
    if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
    if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
    return params;
  }, [filters]);

  const handleExport = useCallback(async () => {
    const res = await MarketGrowthAdminApi.exportAttributions(buildExportParams());
    const { error: err, data } = MarketGrowthAdminApi.normalize(
      res,
      "خروجی اکسل رهگیری معرف‌ها ناموفق بود."
    );
    if (err) throw new Error(err);
    openMarketGrowthExportLink(data);
  }, [buildExportParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getAttributions(buildParams());
      const { error: err, data } = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری رهگیری معرف‌ها ناموفق بود."
      );
      if (err) {
        setError(err);
        setItems([]);
        setTotalCount(0);
        return;
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalCount(Number(data?.totalCount) || 0);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    load();
  }, [load]);

  const partnerLabelMap = useMemo(
    () =>
      partners.reduce((acc, p) => {
        const name =
          p.displayName ||
          p.DisplayName ||
          `کاربر ${p.userId ?? p.UserId}`;
        const code = p.referralCode || p.ReferralCode || p.id;
        acc[p.id] = `${name} (${code})`;
        return acc;
      }, {}),
    [partners]
  );

  const tableRows = useMemo(
    () =>
      items.map((row) => {
        const partnerName =
          row.partnerDisplayName ||
          (row.partnerId ? `نماینده ${row.partnerId}` : "—");
        const parentPartnerName = row.parentPartnerDisplayName || "—";
        const registeredUserName = row.registeredUserId
          ? row.registeredUserDisplayName || `کاربر ${row.registeredUserId}`
          : "—";

        return {
          ...row,
          partnerName,
          partnerMobile: row.partnerMobile || "",
          partnerSearchText: [partnerName, row.partnerMobile].filter(Boolean).join(" "),
          parentPartnerName,
          parentPartnerMobile: row.parentPartnerMobile || "",
          parentPartnerSearchText: [parentPartnerName, row.parentPartnerMobile]
            .filter(Boolean)
            .join(" "),
          registeredUserName,
          registeredUserMobile: row.registeredUserMobile || "",
          registeredUserSearchText: [registeredUserName, row.registeredUserMobile]
            .filter(Boolean)
            .join(" "),
          statusLabel: getAttributionStatusLabel(row.status),
          relatedProjectLabel: row.relatedProjectId ? String(row.relatedProjectId) : "—",
        };
      }),
    [items]
  );

  const headCells = useMemo(
    () => [
      {
        id: "referralCode",
        label: "کد معرف",
        render: (value) => <CopyableValue value={value} />,
      },
      {
        id: "partnerSearchText",
        label: "نماینده / معرف",
        render: (_, row) => (
          <>
            {formatPersonCell(row.partnerName, row.partnerMobile)}
            {row.isSubPartner ? (
              <Chip size="small" label="زیرمجموعه" sx={{ mt: 0.5 }} />
            ) : null}
          </>
        ),
      },
      {
        id: "parentPartnerSearchText",
        label: "مدیر L1 (در صورت زیرمجموعه)",
        render: (_, row) => formatPersonCell(row.parentPartnerName, row.parentPartnerMobile),
      },
      {
        id: "registeredUserSearchText",
        label: "کاربر ثبت‌نام‌شده",
        render: (_, row) =>
          formatPersonCell(row.registeredUserName, row.registeredUserMobile),
      },
      {
        id: "relatedProjectLabel",
        label: "پروژه",
        render: (value, row) =>
          row.relatedProjectId ? <CopyableValue value={row.relatedProjectId} /> : "—",
      },
      {
        id: "statusLabel",
        label: "وضعیت",
        type: "chip",
      },
      {
        id: "firstSeenAt",
        label: "اولین ورود",
        type: "date",
      },
      {
        id: "lastSeenAt",
        label: "آخرین ورود",
        type: "date",
      },
      {
        id: "registeredAt",
        label: "تاریخ ثبت‌نام",
        type: "date",
      },
      {
        id: "projectCreatedAt",
        label: "تاریخ ایجاد پروژه",
        type: "date",
      },
      {
        id: "expiresAt",
        label: "انقضا",
        type: "date",
      },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <InfoIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setDetailRow(row),
        color: "primary",
        tooltip: "جزئیات",
      },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else load();
  };

  return (
    <MarketGrowthPageShell
      title="رهگیری معرف‌ها"
      description="سوابق بازدید، ثبت‌نام و درخواست پروژه از لینک معرف. هر بازدیدکننده یک‌بار نمایش داده می‌شود (آخرین وضعیت)."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="کد معرف"
                value={filters.referralCode}
                onChange={(e) => setFilters((f) => ({ ...f, referralCode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>نماینده</InputLabel>
                <Select
                  label="نماینده"
                  value={filters.partnerId}
                  onChange={(e) => setFilters((f) => ({ ...f, partnerId: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {partners.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {partnerLabelMap[p.id]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                label="کاربر"
                value={filters.registeredUserId}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, registeredUserId: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                label="پروژه"
                value={filters.relatedProjectId}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, relatedProjectId: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  label="وضعیت"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(REFERRAL_ATTRIBUTION_STATUS_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) => setFilters((f) => ({ ...f, fromDate: value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) => setFilters((f) => ({ ...f, toDate: value }))}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={applyFilters}>
                  اعمال فیلتر
                </Button>
                <Button variant="outlined" startIcon={<SyncIcon />} onClick={load}>
                  بروزرسانی
                </Button>
                <MarketGrowthExportButton onExport={handleExport} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            pageSize={PAGE_SIZE}
            showPaging={false}
            defaultSort={{ orderBy: "lastSeenAt", order: "desc" }}
            tableMinWidth={1400}
          />

          <MarketGrowthServerPagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      <AttributionDetailDialog
        open={Boolean(detailRow)}
        data={detailRow}
        onClose={() => setDetailRow(null)}
      />
    </MarketGrowthPageShell>
  );
};

export default ReferralAttributionsPage;
